import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import type { ReactNode, ElementType } from 'react';
import NavLink from "@/Components/NavLink";
import Icon from "@/Components/ui/Icon";

/** Menu alignment relative to the trigger. Maps to the existing ltr:right-0/ltr:left-0
 * origin classes (§3 Dropdown row). Default `end` is byte-identical to today's hardcoded
 * `ltr:right-0 rtl:left-0 origin-top-right`. */
export type DropdownAlign = "start" | "end";

export const ALIGN_CLASS: Record<DropdownAlign, string> = {
    end: "ltr:right-0 rtl:left-0 origin-top-right",
    start: "ltr:left-0 rtl:right-0 origin-top-left",
};

interface DropdownItem {
    label?: ReactNode;
    link?: string;
    icon?: string;
    hasDivider?: boolean;
    onClick?: () => void;
}

const defaultOnClick = () => null;

/** Props shared by the two link components the panel can render an item as
 * (NavLink in Dropdown, Inertia's Link in Split-dropdown). */
type DropdownPanelLinkProps = {
    href: string;
    className?: string;
    children?: ReactNode;
};

export interface DropdownPanelItem {
    label?: ReactNode;
    link?: string;
    icon?: string;
    hasDivider?: boolean;
    onClick?: () => void;
}

interface DropdownPanelProps {
    children?: ReactNode;
    items?: DropdownPanelItem[];
    classItem: string;
    classMenuItems: string;
    alignClass: string;
    /** Component used to render items with a `link` — NavLink for Dropdown, Inertia Link for Split-dropdown. */
    LinkComponent: ElementType<DropdownPanelLinkProps>;
    /** Preserves a known behavioral difference: Dropdown attaches an onClick
     * handler to non-link items, Split-dropdown never did. Default true
     * (Dropdown's prior behavior); Split-dropdown passes false. */
    withItemOnClick?: boolean;
}

/** Shared Transition + Menu.Items + item rendering used by both Dropdown and
 * Split-dropdown. Extracted verbatim — every className below is unchanged
 * from the two call sites, so markup/classes stay byte-identical. */
export const DropdownPanel = ({
    children,
    items,
    classItem,
    classMenuItems,
    alignClass,
    LinkComponent,
    withItemOnClick = true,
}: DropdownPanelProps) => {
    return (
        <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
        >
            <Menu.Items
                className={`absolute ${alignClass} border border-slate-100
            rounded bg-white dark:bg-neutral-800 dark:border-neutral-700 shadow-dropdown z-dropdown
            ${classMenuItems}
            `}
            >
                <div>
                    {children
                        ? children
                        : items?.map((item, index) => (
                            <Menu.Item key={index}>
                                {({ active }) => (
                                    <div
                                        className={`${active
                                            ? "bg-slate-100 text-neutral-900 dark:bg-neutral-600 dark:text-slate-300 dark:bg-opacity-50"
                                            : "text-neutral-600 dark:text-slate-300"
                                            } block     ${item.hasDivider
                                                ? "border-t border-slate-100 dark:border-neutral-700"
                                                : ""
                                            }`}
                                    >
                                        {item.link ? (
                                            <LinkComponent
                                                href={item.link}
                                                className={`block ${classItem}`}
                                            >
                                                {item.icon ? (
                                                    <div className="flex items-center">
                                                        <span className="block text-xl ltr:mr-3 rtl:ml-3">
                                                            <Icon icon={item.icon} />
                                                        </span>
                                                        <span className="block text-sm">
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="block text-sm">
                                                        {item.label}
                                                    </span>
                                                )}
                                            </LinkComponent>
                                        ) : (
                                            <div
                                                className={`block cursor-pointer ${classItem}`}
                                                {...(withItemOnClick
                                                    ? { onClick: item?.onClick || defaultOnClick }
                                                    : {})}
                                            >
                                                {item.icon ? (
                                                    <div className="flex items-center">
                                                        <span className="block text-xl ltr:mr-3 rtl:ml-3">
                                                            <Icon icon={item.icon} />
                                                        </span>
                                                        <span className="block text-sm">
                                                            {item.label}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="block text-sm">
                                                        {item.label}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Menu.Item>
                        ))}
                </div>
            </Menu.Items>
        </Transition>
    );
};

interface DropdownProps {
    label?: ReactNode;
    wrapperClass?: string;
    labelClass?: string;
    children?: ReactNode;
    classMenuItems?: string;
    items?: DropdownItem[];
    classItem?: string;
    className?: string;
    /** Menu alignment. Default `end` — byte-identical to prior hardcoded right/top-right. */
    align?: DropdownAlign;
}

const Dropdown = ({
    label = "Dropdown",
    wrapperClass = "inline-block",
    labelClass = "label-class-custom",
    children,
    classMenuItems = "mt-2 w-[220px]",
    items = [
        {
            label: "Action",
            link: "#",
        },
        {
            label: "Another action",
            link: "#",
        },
        {
            label: "Something else here",
            link: "#",
        },
    ],
    classItem = "px-4 py-2",
    className = "",
    align = "end",
}: DropdownProps) => {
    const alignClass = ALIGN_CLASS[align] ?? ALIGN_CLASS.end;

    return (
        <div className={`relative ${wrapperClass}`}>
            <Menu as="div" className={`block w-full ${className}`}>
                <Menu.Button as="div" role="button" tabIndex={0} className="block w-full">
                    {typeof label === "string" ? (<div className={labelClass}>{label}</div>) : label}
                </Menu.Button>

                <DropdownPanel
                    items={items}
                    classItem={classItem}
                    classMenuItems={classMenuItems}
                    alignClass={alignClass}
                    LinkComponent={NavLink}
                    withItemOnClick
                >
                    {children}
                </DropdownPanel>
            </Menu>
        </div>
    );
};

export default Dropdown;
